/**
 * UiPreferencesStore — namespaced localStorage persistence for shell UI state.
 *
 * Provides two compatible APIs:
 *
 * 1. Zustand reactive hook (for contextual shell panel state and unit tests).
 *
 * 2. Factory / seam interface (for FmContextPanelProvider + FmShellLayout):
 *      const store = createLocalStorageUiPreferencesStore(userId);
 *      const prefs = store.get();
 *
 * Server-persistence (optional future GET/PUT /api/v1/users/me/ui-preferences)
 * slots in behind UiPreferencesStore seam — NOT required for E1.
 *
 * Defaults: contextual panels closed.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";

// Shared types

export interface UiPreferences {
  /** Optional user-level theme override (e.g. force dark on a light route). */
  theme_override?: "dark" | "light";
  /** Whether the on-demand icon-rail contextual panel is open. Default: false. */
  iconRailPanelOpen: boolean;
  /** Which contextual panel tab is active (null = none). Default: null. */
  iconRailActivePanel: string | null;
}

/** Seam interface — factory impl (or future server impl) satisfies this. */
export interface UiPreferencesStore {
  get(): UiPreferences;
  set(partial: Partial<UiPreferences>): void;
}

export const DEFAULTS: UiPreferences = {
  iconRailPanelOpen: false,
  iconRailActivePanel: null,
};

/**
 * Public defaults constant — useful for consumers that need the default shape
 * without instantiating a store (e.g. SSR / test fixtures).
 */
export const UI_PREFERENCES_DEFAULTS: UiPreferences = { ...DEFAULTS };

// 1. Zustand reactive store

interface UiPreferencesActions {
  set: (prefs: Partial<UiPreferences>) => void;
  reset: () => void;
}

type UiPreferencesZustand = UiPreferences & UiPreferencesActions;

export const useUiPreferences = create<UiPreferencesZustand>()(
  persist(
    (set) => ({
      ...DEFAULTS,
      set: (prefs) => set((state) => ({ ...state, ...prefs })),
      reset: () => set((state) => ({ ...state, ...DEFAULTS })),
    }),
    {
      name: "fm-ui-preferences",
      version: 1,
    },
  ),
);

// 2. Factory / seam interface

function storageKey(userId: string): string {
  return `fm:ui-prefs:${userId}`;
}

function sanitize(raw: unknown): UiPreferences {
  if (typeof raw !== "object" || raw === null) return { ...DEFAULTS };
  const obj = raw as Record<string, unknown>;
  return {
    iconRailPanelOpen:
      typeof obj.iconRailPanelOpen === "boolean"
        ? obj.iconRailPanelOpen
        : DEFAULTS.iconRailPanelOpen,
    iconRailActivePanel:
      typeof obj.iconRailActivePanel === "string"
        ? obj.iconRailActivePanel
        : DEFAULTS.iconRailActivePanel,
    ...(obj.theme_override === "dark" || obj.theme_override === "light"
      ? { theme_override: obj.theme_override }
      : {}),
  };
}

/**
 * createLocalStorageUiPreferencesStore — per-user localStorage-backed impl.
 * Keyed per userId so prefs don't bleed across accounts on shared devices.
 * On parse error, resets to defaults — never propagates corrupt state.
 */
export function createLocalStorageUiPreferencesStore(
  userId: string,
): UiPreferencesStore {
  const key = storageKey(userId);

  function get(): UiPreferences {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return { ...DEFAULTS };
      return sanitize(JSON.parse(raw));
    } catch {
      // Parse error — reset to defaults
      localStorage.removeItem(key);
      return { ...DEFAULTS };
    }
  }

  function set(partial: Partial<UiPreferences>): void {
    const next = { ...get(), ...partial };
    localStorage.setItem(key, JSON.stringify(next));
  }

  return { get, set };
}

/**
 * Backward-compat alias — accepts string | null; null maps to anonymous store.
 * Prefer createLocalStorageUiPreferencesStore for new callers.
 */
export function createLocalStorageUiPreferences(
  userId: string | null,
): UiPreferencesStore {
  return createLocalStorageUiPreferencesStore(userId ?? "__anon__");
}

/** Anonymous/unauthenticated fallback: single shared key. */
export const anonymousUiPreferencesStore: UiPreferencesStore =
  createLocalStorageUiPreferencesStore("__anon__");
